/**
 * Группировка замечаний качества по коду.
 *
 * Экран качества показывал 154 строки одним списком: 28 тысяч точек по высоте,
 * каждая строка с абзацем текста. Прочитать такое нельзя — можно только
 * пролистать. При этом сами замечания по коду однотипны: одно и то же правило,
 * сработавшее на разных строках источника. Ответ, ради которого экран
 * открывают, — «каких замечаний сколько и насколько они тяжёлые»; сами строки
 * нужны потом и по одному коду за раз.
 *
 * Что здесь зафиксировано:
 *
 * * **порядок групп задаёт тяжесть, а не количество.** Один блокер важнее
 *   сорока примечаний, и сортировка по числу молча перевернула бы это;
 * * **тяжесть группы — максимальная в ней, а не средняя.** Группа с одним
 *   блокером и сорока примечаниями остаётся группой с блокером: усреднение
 *   спрятало бы его;
 * * **статусы считаются, а не сворачиваются.** «Открыто 3, отложено 2» — два
 *   разных утверждения о мире, и одно число вместо двух выбрало бы за
 *   человека, какое из них важнее.
 */

import type { IssueOut } from "../../api/client";
import type { ShareSegment } from "../../components/StackedShare";
import { severityLabel, severityRank, statusLabel } from "../../lib/epistemic";

export interface IssueGroup {
  code: string;
  items: IssueOut[];
  /** Худшая тяжесть в группе — по ней группа и стоит в списке. */
  severity: string;
  /** Сколько замечаний каждого статуса. Порядок — первого появления. */
  byStatus: { key: string; count: number }[];
  /** Сколько разных объектов задето: одно правило на 40 строках — не 40 бед. */
  subjects: number;
}

export function groupIssues(rows: ReadonlyArray<IssueOut>): IssueGroup[] {
  const index = new Map<string, IssueGroup>();

  for (const issue of rows) {
    const key = issue.issue_code;
    const group = index.get(key) ?? {
      code: key,
      items: [],
      severity: issue.severity,
      byStatus: [],
      subjects: 0,
    };
    group.items.push(issue);
    if (severityRank(issue.severity) < severityRank(group.severity)) {
      group.severity = issue.severity;
    }
    const bucket = group.byStatus.find((row) => row.key === issue.status);
    if (bucket) bucket.count += 1;
    else group.byStatus.push({ key: issue.status, count: 1 });
    index.set(key, group);
  }

  for (const group of index.values()) {
    group.subjects = new Set(
      group.items.map((issue) => `${issue.subject_type}:${issue.subject_key ?? ""}`),
    ).size;
  }

  return [...index.values()].sort((left, right) => {
    const bySeverity = severityRank(left.severity) - severityRank(right.severity);
    if (bySeverity !== 0) return bySeverity;
    if (left.items.length !== right.items.length) {
      return right.items.length - left.items.length;
    }
    return left.code.localeCompare(right.code);
  });
}

/* ------------------------------------------------------------------ */
/* Разрезы выборки замечаний                                           */
/* ------------------------------------------------------------------ */

/** Счётчик разреза ровно как его отдал сервер (`CountBucket`). */
export interface FacetBucket {
  key: string;
  count: number;
}

/**
 * Сегменты 100%-полосы «по тяжести».
 *
 * Порядок задаёт СМЫСЛ, а не величина: тяжесть идёт по тяжести, иначе полоса
 * начинала бы читаться как рейтинг частоты. Неизвестный серверу ключ остаётся
 * собой и в сегмент всё равно попадает — иначе он молча исчез бы из
 * знаменателя.
 *
 * Функция вынесена из экрана ровно потому, что разрез теперь показывают ДВА
 * места: раздел «Что здесь настоящее» (раскрытие) и сам разбор замечаний. Две
 * копии этой сборки однажды разошлись бы порядком — и один и тот же разрез
 * читался бы на двух экранах по-разному.
 */
export function severitySegments(
  buckets: ReadonlyArray<FacetBucket>,
): ShareSegment[] {
  return [...buckets]
    .sort((left, right) => severityRank(left.key) - severityRank(right.key))
    .map((bucket) => ({
      key: bucket.key,
      label: severityLabel(bucket.key).label,
      count: bucket.count,
    }));
}

/** Сегменты 100%-полосы «по статусу разбора». Порядок — серверный. */
export function statusSegments(
  buckets: ReadonlyArray<FacetBucket>,
): ShareSegment[] {
  return buckets.map((bucket) => ({
    key: bucket.key,
    label: statusLabel(bucket.key).label,
    count: bucket.count,
  }));
}

/**
 * Коды замечаний как бары, а не как содержимое выпадающего списка (PF-09).
 *
 * Код внутри `<select>` виден только тому, кто уже собрался фильтровать: чтобы
 * узнать, какое правило сработало чаще всех, приходилось открывать список и
 * читать его глазами. Бар отвечает на это до нажатия.
 *
 * Порядок — по количеству: здесь вопрос именно «чего больше всего», и это
 * единственный разрез замечаний, где величина и есть смысл. Доля считается от
 * ВСЕХ замечаний среза, а не от показанных: знаменатель называется рядом.
 */
export function codeBars(
  buckets: ReadonlyArray<FacetBucket>,
  limit: number,
): {
  shown: FacetBucket[];
  hidden: number;
  /** Самый частый код: относительно него и рисуется длина полосы. */
  top: number;
  /** Сколько замечаний осталось за пределом показа — скрытое названо числом. */
  hiddenCount: number;
} {
  const sorted = [...buckets].sort((left, right) => {
    if (right.count !== left.count) return right.count - left.count;
    return left.key.localeCompare(right.key);
  });
  const shown = sorted.slice(0, limit);
  const rest = sorted.slice(limit);
  return {
    shown,
    hidden: rest.length,
    // Длина полосы — доля от САМОГО ЧАСТОГО кода, а не от всех замечаний:
    // иначе редкие коды слились бы в невидимую полоску, и полоса перестала бы
    // отвечать на «чего больше». Само число стоит рядом с полосой всегда.
    top: sorted[0]?.count ?? 0,
    hiddenCount: rest.reduce((sum, bucket) => sum + bucket.count, 0),
  };
}

/* ------------------------------------------------------------------ */
/* Подписи предмета замечания                                          */
/* ------------------------------------------------------------------ */

